import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    FiPlus,
    FiSearch,
    FiX,
    FiTrash2,
    FiEdit2,
    FiRefreshCw,
    FiCalendar,
    FiCheckCircle,
    FiFileText
} from 'react-icons/fi';
import { getAllEvents, deleteEvent, publishEvent } from './eventService';
import { getAllGuilds } from '../guilds/guildService';
import { EVENT_TYPES, EVENT_MODES, EVENT_STATUSES } from '../shared/constants';
import '../doctors/DoctorList.css';

export default function EventList() {
    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [guilds, setGuilds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterGuild, setFilterGuild] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    const navigate = useNavigate();

    const applyFilters = useCallback(() => {
        let filtered = [...events];

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(e =>
                (e.title?.toLowerCase().includes(searchLower)) ||
                (e.description?.toLowerCase().includes(searchLower))
            );
        }

        if (filterGuild) {
            filtered = filtered.filter(e =>
                e.targetGuildIds?.includes(filterGuild) ||
                e.targetGuildIds?.includes('ALL')
            );
        }

        if (filterType) {
            filtered = filtered.filter(e => e.type === filterType);
        }

        if (filterStatus) {
            filtered = filtered.filter(e => e.status === filterStatus);
        }

        setFilteredEvents(filtered);
    }, [events, searchTerm, filterGuild, filterType, filterStatus]);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    async function loadData() {
        try {
            setLoading(true);
            const [eventsData, guildsData] = await Promise.all([
                getAllEvents(),
                getAllGuilds(),
            ]);
            setEvents(eventsData);
            setGuilds(guildsData);
            setError('');
        } catch (err) {
            setError('Failed to load events');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }


    async function handleDelete(id, event) {
        event.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this event?')) {
            return;
        }

        try {
            await deleteEvent(id);
            await loadData();
        } catch (err) {
            alert('Failed to delete event');
            console.error(err);
        }
    }

    async function handlePublish(id, event) {
        event.stopPropagation();
        if (!window.confirm('Publish this event? It will be visible to target guilds.')) {
            return;
        }

        try {
            await publishEvent(id);
            await loadData();
        } catch (err) {
            alert('Failed to publish event');
            console.error(err);
        }
    }

    function handleEdit(id, event) {
        if (event) event.stopPropagation();
        navigate(`/dashboard/events/edit/${id}`);
    }

    function formatDate(timestamp) {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        if (isNaN(date.getTime())) return 'N/A';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    function getGuildNames(targetGuildIds) {
        if (!targetGuildIds || targetGuildIds.length === 0) return 'None';
        if (targetGuildIds.includes('ALL')) return 'All Guilds';
        return targetGuildIds
            .map(id => guilds.find(g => g.id === id)?.name || id)
            .join(', ');
    }

    if (loading) {
        return (
            <div className="page-container">
                <div className="loading-spinner">
                    <FiRefreshCw className="spin-icon" />
                    <p>Loading events...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left">
                    <h1>Events</h1>
                </div>
                <Link to="/dashboard/events/create" className="btn-primary">
                    <FiPlus />
                    <span>Create Event</span>
                </Link>
            </div>

            {error && (
                <div className="error-message">
                    <FiX />
                    {error}
                </div>
            )}

            {/* Filters Section */}
            <div className="filters-section">
                <div className="search-box">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by title or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <select
                    value={filterGuild}
                    onChange={(e) => setFilterGuild(e.target.value)}
                    className="filter-select"
                >
                    <option value="">All Guilds</option>
                    <option value="ALL">All Guilds (Target)</option>
                    {guilds.map(guild => (
                        <option key={guild.id} value={guild.id}>{guild.name}</option>
                    ))}
                </select>

                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="filter-select"
                >
                    <option value="">All Types</option>
                    {EVENT_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                    ))}
                </select>

                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="filter-select"
                >
                    <option value="">All Statuses</option>
                    {EVENT_STATUSES.map(status => (
                        <option key={status} value={status}>{status}</option>
                    ))}
                </select>

                {(searchTerm || filterGuild || filterType || filterStatus) && (
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setFilterGuild('');
                            setFilterType('');
                            setFilterStatus('');
                        }}
                        className="btn-clear-filters"
                    >
                        <FiX />
                        <span>Clear Filters</span>
                    </button>
                )}
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Title & Description</th>
                            <th>Type</th>
                            <th>Date</th>
                            <th>Mode</th>
                            <th>Target Guilds</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredEvents.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="empty-state">
                                    <div className="empty-content">
                                        <FiCalendar className="empty-icon" />
                                        <p>
                                            {searchTerm || filterGuild || filterType || filterStatus
                                                ? 'No events match your filters.'
                                                : 'No events found. Create your first event.'}
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredEvents.map((event) => (
                                <tr key={event.id}>
                                    <td className="name-cell">
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <strong>{event.title || 'Untitled'}</strong>
                                            <small style={{ color: '#64748b', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {event.description || 'No description'}
                                            </small>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`badge ${event.type === 'CME' ? 'badge-info' : 'badge-secondary'}`}>
                                            {event.type}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FiCalendar style={{ color: 'var(--theme-primary)' }} />
                                            <span>{formatDate(event.date)}</span>
                                        </div>
                                    </td>
                                    <td>{event.mode}</td>
                                    <td className="guilds-cell">
                                        <div style={{ fontSize: '13px' }}>
                                            {getGuildNames(event.targetGuildIds)}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`badge ${event.status === 'Published' ? 'badge-success' : 'badge-warning'}`}>
                                            {event.status}
                                        </span>
                                    </td>
                                    <td className="actions-cell">
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={(e) => handleEdit(event.id, e)}
                                                className="btn-edit"
                                                title="Edit event"
                                            >
                                                <FiEdit2 />
                                            </button>
                                            {event.status === 'Draft' && (
                                                <button
                                                    onClick={(e) => handlePublish(event.id, e)}
                                                    className="btn-edit"
                                                    style={{ color: '#2E7D32', borderColor: '#C8E6C9' }}
                                                    title="Publish event"
                                                >
                                                    <FiCheckCircle />
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => handleDelete(event.id, e)}
                                                className="btn-delete"
                                                title="Delete event"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
